// src/components/QuickIdeaInput.jsx
// Quick input field for capturing new ideas at the top of the home view
import React, { useState } from 'react';
import { Lightbulb, ArrowRight, Loader2 } from 'lucide-react';

function QuickIdeaInput({ onSubmit, disabled = false }) {
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const trimmedDescription = description.trim();
    if (trimmedDescription.length < 10) {
      return; // Minimum 10 characters
    }

    setIsSubmitting(true);
    try {
      await onSubmit(trimmedDescription);
      setDescription(''); // Clear after successful submit
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    // Submit on Enter (without Shift for multi-line)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const isValid = description.trim().length >= 10;
  const charCount = description.trim().length;

  return (
    <form onSubmit={handleSubmit} className="w-full mb-6">
      <div className="relative">
        {/* Icon */}
        <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
          <Lightbulb className="w-5 h-5 text-gray-400 dark:text-gray-500" />
        </div>

        {/* Input */}
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="What's your idea? (min 10 characters)"
          disabled={disabled || isSubmitting}
          className="w-full pl-12 pr-24 py-4 text-lg bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all placeholder:text-gray-400 dark:placeholder:text-gray-500 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
        />

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!isValid || disabled || isSubmitting}
          className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 bg-primary hover:bg-primary/90 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white rounded-lg font-medium transition-all disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="hidden sm:inline">...</span>
            </>
          ) : (
            <>
              <span className="hidden sm:inline">Submit</span>
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>

      {/* Character count hint */}
      {description.length > 0 && charCount < 10 && (
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {10 - charCount} more characters needed
        </p>
      )}
    </form>
  );
}

export default QuickIdeaInput;
